// src/pages/Main/Features/translations.ts
import {
    ApiOutlined,
    IssuesCloseOutlined,
    BarChartOutlined,
    DashboardOutlined,
    RobotOutlined,
    SafetyCertificateOutlined,
} from '@ant-design/icons'

interface FeatureItem {
    icon: React.ComponentType<any>
    title: string
    text: string
}

interface Translations {
    [key: string]: {
        header: string
        features: FeatureItem[]
    }
}

export const translations: Translations = {
    en: {
        header: 'Why traders choose RealColibri',
        features: [
            {
                icon: RobotOutlined,
                title: 'Built on 20+ years of trading',
                text: 'A proprietary high-frequency algorithm distilled from two decades on global futures markets — not a backtested theory, but a strategy that has traded through every kind of market.',
            },
            {
                icon: ApiOutlined,
                title: 'From signup to first trade in 10 minutes',
                text: 'Create your RealColibri account, connect your exchange via Read & Trade API (or login for Rithmic), pick a symbol, press Start. That is the whole onboarding.',
            },
            {
                icon: DashboardOutlined,
                title: 'Earn from day one',
                text: 'No setup fees, no learning curve, no months of paper trading. The algorithm starts working on your first connected deposit, daily P&L visible in your dashboard.',
            },
            {
                icon: BarChartOutlined,
                title: '~1,000 trades per day per account',
                text: 'Hundreds of micro-decisions per hour — entry, sizing, exit — all calculated and executed automatically. Every parameter recorded and auditable in your deals history.',
            },
            {
                icon: IssuesCloseOutlined,
                title: 'Risk-managed by design',
                text: 'Position sizing scales with volatility. Stop logic protects against drawdown spikes. Built to survive bear markets, flash crashes and weekend gaps — not just thrive in bull runs.',
            },
            {
                icon: SafetyCertificateOutlined,
                title: 'You stay in custody — always',
                text: 'We never hold your funds, never ask for withdrawal rights, never see your password. Read & Trade API only. Disconnect at any time, your balance stays put on your exchange.',
            },
        ],
    },
    es: {
        header: 'Por qué los traders eligen RealColibri',
        features: [
            {
                icon: RobotOutlined,
                title: 'Construido con 20+ años de trading',
                text: 'Un algoritmo propio de alta frecuencia destilado de dos décadas en los mercados globales de futuros — no es una teoría con backtest, sino una estrategia que ha operado en todo tipo de mercados.',
            },
            {
                icon: ApiOutlined,
                title: 'Del registro al primer trade en 10 minutos',
                text: 'Crea tu cuenta RealColibri, conecta tu exchange con API Read & Trade (o login para Rithmic), elige un símbolo, presiona Start. Ese es todo el onboarding.',
            },
            {
                icon: DashboardOutlined,
                title: 'Genera desde el primer día',
                text: 'Sin tarifas de configuración, sin curva de aprendizaje, sin meses de paper trading. El algoritmo empieza a operar con tu primer depósito conectado, P&L diario visible en tu panel.',
            },
            {
                icon: BarChartOutlined,
                title: '~1.000 operaciones al día por cuenta',
                text: 'Cientos de micro-decisiones por hora — entrada, tamaño, salida — todas calculadas y ejecutadas automáticamente. Cada parámetro queda registrado y auditable en tu historial.',
            },
            {
                icon: IssuesCloseOutlined,
                title: 'Riesgo gestionado por diseño',
                text: 'El tamaño de posición escala con la volatilidad. La lógica de stop protege contra picos de drawdown. Diseñado para sobrevivir a bear markets, flash crashes y gaps de fin de semana — no solo para brillar en bull runs.',
            },
            {
                icon: SafetyCertificateOutlined,
                title: 'Mantienes la custodia — siempre',
                text: 'Nunca tenemos tus fondos, nunca pedimos derechos de retiro, nunca vemos tu contraseña. Solo API Read & Trade. Desconecta cuando quieras, tu saldo se queda en tu exchange.',
            },
        ],
    },
    ru: {
        header: 'Почему трейдеры выбирают RealColibri',
        features: [
            {
                icon: RobotOutlined,
                title: 'Построен на 20+ годах торговли',
                text: 'Собственный высокочастотный алгоритм, выкристаллизованный из двух десятилетий на глобальных фьючерсных рынках — не теория с бэктестом, а стратегия, прошедшая через рынки любого типа.',
            },
            {
                icon: ApiOutlined,
                title: 'От регистрации до первой сделки за 10 минут',
                text: 'Создайте аккаунт RealColibri, подключите биржу через Read & Trade API (или login для Rithmic), выберите символ, нажмите Start. Это весь онбординг.',
            },
            {
                icon: DashboardOutlined,
                title: 'Зарабатывайте с первого дня',
                text: 'Без платы за настройку, без кривой обучения, без месяцев paper-trading. Алгоритм начинает работать с первого подключённого депозита, дневной P&L виден в кабинете.',
            },
            {
                icon: BarChartOutlined,
                title: '~1 000 сделок в день на аккаунт',
                text: 'Сотни микро-решений в час — вход, объём, выход — всё рассчитывается и исполняется автоматически. Каждый параметр сохраняется и доступен в истории сделок.',
            },
            {
                icon: IssuesCloseOutlined,
                title: 'Управление риском «по дизайну»',
                text: 'Размер позиции масштабируется с волатильностью. Stop-логика защищает от всплесков просадки. Создан переживать медвежьи рынки, flash crash и weekend-гэпы — не только сиять в бычьих трендах.',
            },
            {
                icon: SafetyCertificateOutlined,
                title: 'Деньги всегда под вашим контролем',
                text: 'Мы не держим ваши средства, не запрашиваем право вывода, не видим ваш пароль. Только Read & Trade API. Отключитесь в любой момент — баланс остаётся на бирже.',
            },
        ],
    },
    hy: {
        header: 'Ինչու են թրեյդերները ընտրում RealColibri',
        features: [
            {
                icon: RobotOutlined,
                title: 'Կառուցված 20+ տարվա առևտրի վրա',
                text: 'Սեփական բարձր հաճախականության ալգորիթմ, թորված երկու տասնամյակների ընթացքում գլոբալ ֆյուչերսային շուկաներից — ոչ թե backtest-ի տեսություն, այլ ստրատեգիա, որն աշխատել է բոլոր տիպի շուկաներում:',
            },
            {
                icon: ApiOutlined,
                title: 'Գրանցումից մինչև առաջին գործարք՝ 10 րոպեում',
                text: 'Ստեղծեք RealColibri հաշիվ, միացրեք բորսան Read & Trade API-ով (կամ Rithmic-ի համար՝ login-ով), ընտրեք սիմվոլ, սեղմեք Start: Սա է ողջ onboarding-ը:',
            },
            {
                icon: DashboardOutlined,
                title: 'Վաստակեք առաջին օրից',
                text: 'Առանց կարգավորման վճարների, առանց ուսուցման կորության, առանց ամիսներով paper-trading-ի: Ալգորիթմը սկսում է աշխատել ձեր առաջին միացված դեպոզիտից, օրական P&L-ը տեսանելի է վահանակում:',
            },
            {
                icon: BarChartOutlined,
                title: '~1 000 գործարք օրական մեկ հաշվով',
                text: 'Հարյուրավոր միկրո-որոշումներ ժամում — մուտք, չափ, ելք — բոլորը հաշվարկվում և կատարվում են ավտոմատ: Յուրաքանչյուր պարամետր արձանագրվում է և տեսանելի է գործարքների պատմությունում:',
            },
            {
                icon: IssuesCloseOutlined,
                title: 'Ռիսկի կառավարում՝ դիզայնով',
                text: 'Դիրքի չափը մասշտաբավորվում է անկայունության հետ: Stop-տրամաբանությունը պաշտպանում է drawdown-ի թռիչքներից: Կառուցված է գոյատևելու արջի շուկաներում, flash crash-երում և weekend-գէփերում — ոչ միայն ցուցադրվել ցուլի թրենդներում:',
            },
            {
                icon: SafetyCertificateOutlined,
                title: 'Դուք միշտ կուստոդիում եք',
                text: 'Մենք չենք պահում ձեր միջոցները, չենք պահանջում դուրսբերման իրավունք, չենք տեսնում ձեր գաղտնաբառը: Միայն Read & Trade API: Անջատվեք ցանկացած ժամանակ — մնացորդը մնում է բորսայում:',
            },
        ],
    },
    de: {
        header: 'Warum Trader RealColibri wählen',
        features: [
            {
                icon: RobotOutlined,
                title: 'Aufgebaut auf 20+ Jahren Handel',
                text: 'Ein eigener Hochfrequenz-Algorithmus, destilliert aus zwei Jahrzehnten an globalen Futures-Märkten — keine Backtest-Theorie, sondern eine Strategie, die jede Marktphase durchhandelt hat.',
            },
            {
                icon: ApiOutlined,
                title: 'Von Anmeldung zum ersten Trade in 10 Minuten',
                text: 'RealColibri-Konto erstellen, Börse per Read & Trade API verbinden (oder Login für Rithmic), Symbol wählen, Start drücken. Das ist das gesamte Onboarding.',
            },
            {
                icon: DashboardOutlined,
                title: 'Verdienen ab Tag eins',
                text: 'Keine Einrichtungsgebühren, keine Lernkurve, kein monatelanges Paper-Trading. Der Algorithmus startet mit Ihrem ersten verbundenen Deposit, täglicher P&L im Dashboard sichtbar.',
            },
            {
                icon: BarChartOutlined,
                title: '~1.000 Trades pro Tag und Konto',
                text: 'Hunderte Mikro-Entscheidungen pro Stunde — Entry, Sizing, Exit — alle automatisch berechnet und ausgeführt. Jeder Parameter wird gespeichert und ist in Ihrer Trade-Historie nachvollziehbar.',
            },
            {
                icon: IssuesCloseOutlined,
                title: 'Risk-Management by design',
                text: 'Positionsgröße skaliert mit Volatilität. Stop-Logik schützt vor Drawdown-Spitzen. Gebaut, um Bärenmärkte, Flash-Crashes und Wochenend-Gaps zu überleben — nicht nur in Bull-Runs zu glänzen.',
            },
            {
                icon: SafetyCertificateOutlined,
                title: 'Sie bleiben immer in Verwahrung',
                text: 'Wir halten nie Ihre Mittel, fordern nie Abhebungsrechte, sehen nie Ihr Passwort. Nur Read & Trade API. Jederzeit trennen — Ihr Guthaben bleibt auf Ihrer Börse.',
            },
        ],
    },
    fr: {
        header: 'Pourquoi les traders choisissent RealColibri',
        features: [
            {
                icon: RobotOutlined,
                title: 'Construit sur 20+ ans de trading',
                text: 'Un algorithme propriétaire haute fréquence distillé sur deux décennies de marchés futures mondiaux — pas une théorie backtestée, mais une stratégie qui a tradé tous types de marchés.',
            },
            {
                icon: ApiOutlined,
                title: 'De l\'inscription au premier trade en 10 minutes',
                text: 'Créez votre compte RealColibri, connectez votre exchange via API Read & Trade (ou login pour Rithmic), choisissez un symbole, appuyez sur Start. C\'est tout l\'onboarding.',
            },
            {
                icon: DashboardOutlined,
                title: 'Gagnez dès le premier jour',
                text: 'Pas de frais de configuration, pas de courbe d\'apprentissage, pas de mois de paper-trading. L\'algorithme démarre avec votre premier dépôt connecté, P&L quotidien visible dans le tableau de bord.',
            },
            {
                icon: BarChartOutlined,
                title: '~1 000 trades par jour et par compte',
                text: 'Des centaines de micro-décisions par heure — entrée, sizing, sortie — toutes calculées et exécutées automatiquement. Chaque paramètre enregistré et auditable dans votre historique.',
            },
            {
                icon: IssuesCloseOutlined,
                title: 'Gestion du risque par conception',
                text: 'La taille de position évolue avec la volatilité. La logique de stop protège contre les pics de drawdown. Conçu pour survivre aux marchés baissiers, flash crashes et gaps du week-end — pas seulement briller en bull run.',
            },
            {
                icon: SafetyCertificateOutlined,
                title: 'Vous gardez la custody — toujours',
                text: 'Nous ne détenons jamais vos fonds, ne demandons jamais le droit de retrait, ne voyons jamais votre mot de passe. API Read & Trade uniquement. Déconnectez à tout moment — votre solde reste sur l\'exchange.',
            },
        ],
    },
    it: {
        header: 'Perché i trader scelgono RealColibri',
        features: [
            {
                icon: RobotOutlined,
                title: 'Costruito su 20+ anni di trading',
                text: 'Un algoritmo proprietario ad alta frequenza distillato da due decenni sui mercati futures globali — non una teoria con backtest, ma una strategia che ha operato in ogni tipo di mercato.',
            },
            {
                icon: ApiOutlined,
                title: 'Dalla registrazione al primo trade in 10 minuti',
                text: 'Crea l\'account RealColibri, collega l\'exchange con API Read & Trade (o login per Rithmic), scegli un simbolo, premi Start. È tutto l\'onboarding.',
            },
            {
                icon: DashboardOutlined,
                title: 'Guadagna dal primo giorno',
                text: 'Nessuna tariffa di setup, nessuna curva di apprendimento, nessun mese di paper-trading. L\'algoritmo parte con il tuo primo deposito connesso, P&L giornaliero visibile nella dashboard.',
            },
            {
                icon: BarChartOutlined,
                title: '~1.000 trade al giorno per account',
                text: 'Centinaia di micro-decisioni all\'ora — entrata, sizing, uscita — tutte calcolate ed eseguite automaticamente. Ogni parametro registrato e verificabile nella tua cronologia.',
            },
            {
                icon: IssuesCloseOutlined,
                title: 'Risk management by design',
                text: 'La dimensione della posizione scala con la volatilità. La logica di stop protegge dai picchi di drawdown. Costruito per sopravvivere a bear market, flash crash e gap del weekend — non solo brillare nei bull run.',
            },
            {
                icon: SafetyCertificateOutlined,
                title: 'Mantieni la custodia — sempre',
                text: 'Non deteniamo mai i tuoi fondi, non chiediamo mai i diritti di prelievo, non vediamo mai la tua password. Solo API Read & Trade. Disconnetti quando vuoi — il saldo resta sull\'exchange.',
            },
        ],
    },
}
